import Decimal from 'decimal.js';

import { businessEntityKinds } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.constants';
import { BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import { businessTaxStatusDisplayNames } from '@/modules/entities/details/businessEntities/businessEntities.constants';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getDisplayNameFromEntityType } from '@/modules/entities/utils/getDisplayNameFromEntityType';
import { getEntityKindFromEntityType } from '@/modules/entities/utils/getEntityKindFromEntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useEntitySyncingState } from '@/modules/estate/hooks/useEntitySyncingState';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';

import {
  sumGrantorControlledValue,
  sumHoldingsGroupCurrentValue,
} from './DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import {
  DirectHoldingsGroupCardVariant,
  DirectHoldingsGroupVariant,
} from './EstateDirectHoldings.types';

// Single source of truth for which holdings group cards each container variant
// renders. Both the rendered cards and the section header total are derived
// from this map, so the header can never list a different set of cards than the
// ones shown beneath it.
export const DIRECT_HOLDINGS_CARDS_BY_VARIANT: Record<
  DirectHoldingsGroupVariant,
  DirectHoldingsGroupCardVariant[]
> = {
  joint: ['joint-account'],
  individual: ['individual-account', 'retirement-account', 'insurance-account'],
  other: [
    'individual-account',
    'retirement-account',
    'insurance-account',
    'custodial-account',
    'qualified-tuition-account',
  ],
};

export function filterHoldingsGroupsByClient(
  entities: EstatePage_EntityFragment[],
  variant: EntityType,
  clientProfileId?: string,
  primaryClientIds?: string[]
): EstatePage_EntityFragment[] {
  return entities.filter((entity) => {
    // Check if entity kind matches the expected kind for the variant
    const expectedKind = getEntityKindFromEntityType(variant);
    if (entity.kind !== expectedKind) {
      return false;
    }

    // For individual-account and retirement-account, check owner
    if (
      entity.subtype.__typename === 'IndividualPersonalAccount' ||
      entity.subtype.__typename === 'RetirementPersonalAccount'
    ) {
      // If no clientProfileId is provided but primaryClientIds is provided,
      // filter to show accounts NOT owned by any primary client (for "Other" bucket)
      if (!clientProfileId && primaryClientIds && primaryClientIds.length > 0) {
        const ownerId = entity.subtype.owner?.individual?.id;
        // Include accounts with no owner or owner not in primary clients
        return !ownerId || !primaryClientIds.includes(ownerId);
      }

      // If no clientProfileId is provided and no primaryClientIds, return false
      if (!clientProfileId) {
        return false;
      }

      return entity.subtype.owner?.individual?.id === clientProfileId;
    }

    // For insurance-account, check if clientProfileId is in owners array
    if (entity.subtype.__typename === 'InsurancePersonalAccount') {
      // If no clientProfileId is provided but primaryClientIds is provided,
      // filter to show accounts NOT owned by any primary client (for "Other" bucket)
      if (!clientProfileId && primaryClientIds && primaryClientIds.length > 0) {
        const ownerIds =
          entity.subtype.owners
            ?.map((owner) => owner.individual?.id)
            .filter((id): id is string => !!id) ?? [];
        // Include accounts with no owners or none of the owners are primary clients
        return (
          ownerIds.length === 0 ||
          !ownerIds.some((id) => primaryClientIds.includes(id))
        );
      }

      if (!clientProfileId) {
        return false;
      }

      return (
        entity.subtype.owners?.some(
          (owner) => owner.individual?.id === clientProfileId
        ) ?? false
      );
    }

    // For other variants, just return true since we already checked the kind
    return true;
  });
}

export function filterTrustsAndBusinessesByClient(
  entities: EstatePage_EntityFragment[],
  clientProfileId?: string
): EstatePage_EntityFragment[] {
  // Only show active revocable trusts and businesses
  if (!clientProfileId) {
    return [];
  }
  return entities.filter((entity) => {
    // Only allow revocable trusts and business entities
    if (entity.subtype.__typename === 'RevocableTrust') {
      return (
        entity.subtype.grantors?.some(
          (grantor) => grantor.individual?.id === clientProfileId
        ) ?? false
      );
    }

    // Allow business entities only if client has a stake
    if (businessEntityKinds.has(entity.kind)) {
      return (
        entity.ownedByStakes?.some(
          (stake) => stake.owningIndividual?.id === clientProfileId
        ) ?? false
      );
    }

    // All other entity types are filtered out
    return false;
  });
}

export interface DirectHoldingsSectionTotalArgs {
  variant: DirectHoldingsGroupVariant;
  clientProfileId?: string;
  primaryClientIds?: string[];
}

/**
 * Computes the section-header total for a direct-holdings container by summing
 * the same entities its cards render: every card variant in
 * `DIRECT_HOLDINGS_CARDS_BY_VARIANT[variant]`, plus — for the `individual`
 * variant — the client's trusts and businesses, which render inside the
 * individual-account card's accordion. Mirrors `TrustContainer`'s "derive the
 * set once, then both sum and render from it" approach so the header total
 * stays in lockstep with the visible cards.
 */
export function computeDirectHoldingsSectionTotal(
  entities: EstatePage_EntityFragment[],
  { variant, clientProfileId, primaryClientIds }: DirectHoldingsSectionTotalArgs
): Decimal {
  const cardEntities = DIRECT_HOLDINGS_CARDS_BY_VARIANT[variant].flatMap(
    (cardVariant) =>
      filterHoldingsGroupsByClient(
        entities,
        cardVariant,
        clientProfileId,
        primaryClientIds
      )
  );
  const cardsTotal = sumHoldingsGroupCurrentValue(cardEntities);

  // Trusts and businesses only render (inside the individual-account card) when
  // the container has a clientProfile — i.e. the `individual` variant — so only
  // fold them into the total in that same case. The clientProfileId is passed
  // through so business stakes are ownership-weighted to match the accordion.
  if (variant !== 'individual' || !clientProfileId) {
    return cardsTotal;
  }

  const trustsAndBusinesses = filterTrustsAndBusinessesByClient(
    entities,
    clientProfileId
  );
  return cardsTotal.plus(
    sumHoldingsGroupCurrentValue(trustsAndBusinesses, [clientProfileId])
  );
}

export function filterBusinesses(
  entities: EstatePage_EntityFragment[]
): EstatePage_EntityFragment[] {
  return entities.filter((entity) => {
    return businessEntityKinds.has(entity.kind);
  });
}

type BusinessOwnershipStake = NonNullable<
  EstatePage_EntityFragment['ownedByStakes']
>[number];

function formatOwnershipPercent(percentage: Decimal): string {
  return `${formatPercent(percentage, BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE)}% ownership`;
}

/**
 * Names the primary-client owners of a business for its subtitle, e.g.
 * "John & Mary Carson: 100.000000% ownership".
 *
 * Names always come from server-computed display names rather than being
 * assembled here, so the label picks up preferred names and drops middle names
 * and suffixes exactly the way the rest of the app does.
 *
 * A household has at most two primary clients, so two primary owners are
 * necessarily *the* household's two primaries — which makes the household's own
 * display name the canonical combined label, and keeps the decision of how to
 * name a couple together in one place on the server.
 *
 * Returns null when there is nothing sensible to say: no primary owners, or any
 * stake missing the owner name or percentage the label is built from.
 */
export function formatPrimaryClientOwnershipLabel(
  primaryClientStakes: BusinessOwnershipStake[],
  householdDisplayName: string | null
): string | null {
  // Keyed by owner id so a client holding more than one stake in the same
  // business is still one owner, with their stakes summed.
  const ownerNamesById = new Map<string, string>();
  let totalPercentage = new Decimal(0);

  for (const stake of primaryClientStakes) {
    const owner = stake?.owningIndividual;
    if (
      !owner?.id ||
      !owner.displayName ||
      stake?.ownershipPercentage == null
    ) {
      return null;
    }
    ownerNamesById.set(owner.id, owner.displayName);
    totalPercentage = totalPercentage.plus(stake.ownershipPercentage);
  }

  const ownerNames = Array.from(ownerNamesById.values());
  if (ownerNames.length === 0) {
    return null;
  }

  if (ownerNames.length === 1) {
    return `${ownerNames[0]}: ${formatOwnershipPercent(totalPercentage)}`;
  }

  // The household name is preferred, but it can be absent while the household
  // context is still loading; naming the owners individually is a correct, if
  // wordier, answer in that window.
  const combinedName = householdDisplayName || ownerNames.join(' & ');
  return `${combinedName}: ${formatOwnershipPercent(totalPercentage)}`;
}

export function useFormattedBusinessSubtitle(
  business: EstatePage_EntityFragment,
  primaryClients: ContextPrimaryClient[] | null,
  householdDisplayName: string | null
): string {
  const doesEntityHaveActiveSync = useEntitySyncingState(business.id);
  const primaryClientIds = primaryClients?.map((client) => client.id) || [];
  const currentValue = sumGrantorControlledValue([business]);
  const parts: string[] = [];

  if (doesEntityHaveActiveSync) {
    parts.push('Syncing...');
  } else {
    parts.push(
      `${formatCurrencyNoDecimals(currentValue)} (client-controlled ownership)`
    );
  }
  const primaryClientStakes = (business?.ownedByStakes ?? []).filter(
    (stake) =>
      stake?.owningIndividual?.id &&
      primaryClientIds.includes(stake.owningIndividual.id)
  );

  const ownershipLabel = formatPrimaryClientOwnershipLabel(
    primaryClientStakes,
    householdDisplayName
  );
  if (ownershipLabel) {
    parts.push(ownershipLabel);
  }

  if ('taxStatus' in business.subtype && business.subtype.taxStatus) {
    parts.push(
      `Tax status: ${businessTaxStatusDisplayNames[business.subtype.taxStatus]}`
    );
  }

  const entityTypeName = getDisplayNameFromEntityType(
    getEntityTypeFromEntityKind(business.kind)
  );
  if (entityTypeName) {
    parts.push(entityTypeName);
  }

  return parts.join(' | ');
}
